'use client';
import React from 'react';
import { Button, Icon } from '@benzinga/core-ui';
import styled from '@benzinga/themetron';
import { NumericFormat } from 'react-number-format';
import { CryptoExchangeRateResponse, ExchangeRate } from '@benzinga/content-manager';
import { faArrowUpArrowDown } from '@fortawesome/pro-solid-svg-icons';

export interface CoinExchangeRateCalculatorProps {
  title: string;
  cta_title?: string;
  cta_description?: string;
  from_currency?: string;
  to_currency?: string;
  link?: string;
  exchange_rates?: CryptoExchangeRateResponse;
}
const inputStyles = {
  backgroundColor: ' #fefefe',
  border: '1px solid #ccc',
  color: '#0a0a0a',
} as React.CSSProperties;

export const CoinExchangeRateCalculator: React.FC<CoinExchangeRateCalculatorProps> = ({
  cta_description,
  cta_title,
  exchange_rates,
  from_currency = 'btc',
  link,
  title,
  to_currency = 'usd',
}) => {
  const [fromCurrency, setFromCurrency] = React.useState(from_currency);
  const [toCurrency, setToCurrency] = React.useState(to_currency);
  // const [exchangeRates, setExchangeRates] = React.useState(exchange_rates?.rates);
  const [convertedValue, setConvertedValue] = React.useState<string>('1');
  const [userValue, setUserValue] = React.useState<number>(1);

  const exchangeRates = exchange_rates?.rates;

  const CoinExchangeOptions = React.useMemo(() => {
    return { convertedValue, exchangeRates, fromCurrency, toCurrency, userValue };
  }, [fromCurrency, convertedValue, toCurrency, exchangeRates, userValue]);

  const handleConversation = React.useCallback(
    async exchangeOptions => {
      if (exchangeRates) {
        //DO CALCULATION
        const btcNode = exchangeRates['btc'] as ExchangeRate;
        const fromNode = exchangeRates[exchangeOptions.fromCurrency] as ExchangeRate;
        const toNode = exchangeRates[exchangeOptions.toCurrency] as ExchangeRate;
        //

        const userInput = Number(exchangeOptions.userValue);
        const fromToBTC: number = (userInput * btcNode?.compare_value) / fromNode.compare_value;

        const converted = (fromToBTC * toNode.compare_value) / btcNode.compare_value;
        setConvertedValue(converted.toLocaleString('en-US', { maximumFractionDigits: 8 }));
      }
    },
    [exchangeRates],
  );

  React.useEffect(() => {
    handleConversation({ ...CoinExchangeOptions });
  }, [handleConversation, CoinExchangeOptions]);

  const onChangeFromCurrency = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFromCurrency(e.target.value);
      handleConversation({ ...CoinExchangeOptions, fromCurrency: e.target.value });
    },
    [handleConversation, CoinExchangeOptions],
  );

  const onChangeToCurrency = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setToCurrency(e.target.value);
      handleConversation({ ...CoinExchangeOptions, toCurrency: e.target.value });
    },
    [handleConversation, CoinExchangeOptions],
  );

  const handleCoinValueChange = React.useCallback(
    (value: number) => {
      setUserValue(Number(value));
      handleConversation({ ...CoinExchangeOptions, userValue: value });
    },
    [handleConversation, CoinExchangeOptions],
  );

  const switchToFrom = React.useCallback(() => {
    const tempfrom = fromCurrency;
    const tempto = toCurrency;
    setFromCurrency(tempto);
    setToCurrency(tempfrom);
    handleConversation({ ...CoinExchangeOptions, fromCurrency: tempfrom, toCurrency: tempto });
  }, [handleConversation, CoinExchangeOptions, fromCurrency, toCurrency]);

  if (!exchange_rates?.rates) {
    return null;
  }

  return (
    <CoinExchangeRateCalculatorContainer>
      <div className="w-100 mb-5">
        <div className="wrap-block flex-col bg-white border border-t-4 shadow-sm rounded-xl">
          <div className="p-4 md:p-5">
            <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
            <div className="grid gap-4 grid-cols-12 grid-flow-row grid-rows-1 w-full justify-between max-width-1200-centered items-ctenter mb-4">
              <div className="md:col-span-6 col-span-12 w-full">
                <div className="input-container flex flex-col mb-2">
                  <NumericFormat
                    className="border p-2 rounded"
                    onValueChange={item => {
                      handleCoinValueChange(Number(item.value));
                    }}
                    thousandSeparator=","
                    thousandsGroupStyle="thousand"
                    value={userValue}
                  />
                </div>
                <div className="input-container flex flex-col mb-2">
                  <select
                    className="relative w-full h-10 box-border block overflow-visible p-2 mb-2 text-base rounded"
                    id="loan_program"
                    name="loan_program"
                    onChange={onChangeFromCurrency}
                    style={inputStyles}
                    value={fromCurrency}
                  >
                    {exchangeRates &&
                      Object.keys(exchangeRates).map((key, i) => {
                        return (
                          <option key={i} value={key}>
                            {exchangeRates[key].coin_name} ({exchangeRates[key]?.unit})
                          </option>
                        );
                      })}
                  </select>
                </div>
                <div className="input-container flex justify-center mb-2">
                  <div className="swap-coin">
                    <Icon icon={faArrowUpArrowDown} onClick={switchToFrom} strokeWidth={0.5} />
                  </div>
                </div>
                <div className="input-container flex flex-col mb-2">
                  <select
                    className="relative w-full h-10 box-border block overflow-visible p-2 mb-2 text-base rounded"
                    id="loan_program"
                    name="loan_program"
                    onChange={onChangeToCurrency}
                    style={inputStyles}
                    value={toCurrency}
                  >
                    {exchangeRates &&
                      Object.keys(exchangeRates).map((key, i) => {
                        return (
                          <option key={i} value={key}>
                            {exchangeRates[key].coin_name} ({exchangeRates[key]?.unit})
                          </option>
                        );
                      })}
                  </select>
                </div>
              </div>
              <div className="md:col-span-6 col-span-12 w-full flex items-center justify-center">
                <div className="result-node input-container flex flex-col mb-2">
                  <div className="price-item">{convertedValue}</div>
                </div>
              </div>
            </div>
            <hr />
            <div className="flex items-center justify-center mt-4">
              <div className="md:basis-3/4 basis-full">
                {cta_title && <h3>{cta_title}</h3>}
                {cta_description && <p>{cta_description}</p>}
              </div>
              <div className="md:basis-1/4 basis-full flex justify-center ">
                <div className="result-node input-container flex flex-col mb-2">
                  {link && (
                    <div className="action-btn">
                      <Button as="a" href={link} size="md" variant="flat-orange">
                        Get Deal
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CoinExchangeRateCalculatorContainer>
  );
};

export const CoinExchangeRateCalculatorContainer = styled.div`
  .wrap-block {
    border-top-color: ${({ theme }) => theme.colorPalette.orange500};
  }
  .input-container {
    label {
      font-weight: 500;
    }
  }
  .result-node {
    h5 {
      font-weight: 500;
    }
    .price-item {
      font-size: 2rem;
    }
  }
  .swap-coin {
    background: #e1ebfa;
    padding: 4px;
    color: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
  }
  .action-btn {
    a {
      padding: 0.75rem 1rem;
    }
  }
`;
